<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ tradeItem ? $t('Subcontract.PackagesDashboard.TradeItemSaveModal.updateTradeItem') : $t('Subcontract.PackagesDashboard.TradeItemSaveModal.createTradeItem') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <form
          action=""
          class="flex flex-col space-y-4"
          @submit.prevent="saveTrade"
        >
          <div>
            <p class="text-lg font-semibold">
              {{ tradeItem ? $t('Subcontract.PackagesDashboard.TradeItemSaveModal.packageLabel') : $t('Subcontract.PackagesDashboard.TradeItemSaveModal.addingToPackage') }} {{ $props.package.name }}
            </p>
          </div>

          <label>
            {{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.label.name') }}
            <AppInput
              v-model="packageTradeItemForm.form.name"
              name="name"
              type="text"
              class="form-input"
              :error="packageTradeItemForm.getErrors('name')"
              data-test="name-input"
            />
          </label>

          <div class="flex space-x-4 w-full">
            <label class="w-full">
              {{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.label.unit') }}

              <UnitsTypeAhead
                v-model="packageTradeItemForm.form.unit"
                name="unit"
                :error="packageTradeItemForm.getErrors('unit')"
              />
            </label>

            <label class="w-full">
              {{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.label.quantity') }}
              <AppInput
                v-model="packageTradeItemForm.form.quantity"
                class="form-input"
                name="quantity"
                type="number"
                :error="packageTradeItemForm.getErrors('quantity')"
              />
            </label>
          </div>

          <label>
            {{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.label.rate') }} <span class="text-sm text-gray-400">{{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.hint.ratePerUnit') }}</span>

            <AppCurrencyInput
              v-model="packageTradeItemForm.form.rate"
              class="form-input"
              name="rate"
              :error="packageTradeItemForm.getErrors('rate')"
            />
          </label>

          <label>
            {{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.label.value') }} <span class="text-sm text-gray-400">{{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.hint.currency') }}</span>

            <CurrencyInput
              v-model="packageTradeItemForm.form.value"
              class="form-input disabled:bg-gray-300"
              name="value"
              disabled
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close'); packageTradeItemForm.resetData()"
        >
          {{ $t('Subcontract.PackagesDashboard.TradeItemSaveModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="createPackageTradeItemAction.is(States.LOADING)"
          data-test="save-trade-item"
          @click="saveTrade"
        >
          {{ tradeItem ? $t('Subcontract.PackagesDashboard.TradeItemSaveModal.button.update') : $t('Subcontract.PackagesDashboard.TradeItemSaveModal.button.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import CurrencyInput from '@/components/app/CurrencyInput.vue';
import Modal from '@/components/Modal.vue';
import UnitsTypeAhead from '@/components/UnitsTypeAhead.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Package from '@/models/Package';
import TradeItem from '@/models/TradeItem';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import Swal from 'sweetalert2';
import { useTemplateRef, watch } from 'vue';

const props = defineProps<{
  package: Package;
  tradeItem?: TradeItem;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const packageTradeItemForm = useFormData({
  rate: undefined,
  quantity: undefined,
  value: undefined,
  trade_item_id: undefined,
  unit: '',
  name: '',
});

watch(() => [packageTradeItemForm.form.rate, packageTradeItemForm.form.quantity], (newValue) => {
  if(newValue[0] > 0 && newValue[1] > 0) {
    packageTradeItemForm.form.value = newValue[0] * newValue[1];
  }
});

const packageTradeItemsStore = usePackageTradeItemsStore();
const projectsStore = useProjectsStore();
const createPackageTradeItemAction = useStoreApiAction(packageTradeItemsStore.create);

const saveTrade = () => {
  packageTradeItemForm.resetErrors();

  createPackageTradeItemAction.request(projectsStore.project.id, props.package.id, packageTradeItemForm.form).then(
    (data) => {
      Swal.fire({
        icon: 'success',
        title: t('Subcontract.PackagesDashboard.TradeItemSaveModal.message.tradeItemAdded'),
      }).then(() => {
        emits('close');
      });
    },
  ).catch((error) => {
    packageTradeItemForm.setErrors(error.data);
  });
};
</script>

<style scoped></style>
